import { apiType } from "./apiType";
import { mapTaskUiStatusToApi } from "@/src/utils/tasks/mapTaskStatus";
import { findTaskInList } from "@/src/utils/tasks/findTaskByExactId";
import { withOfficeUrlQuery } from "@/src/utils/accessControl/formatOfficeUrl";
import { withOfficeQuery } from "@/src/utils/tasks/normalizeFormOfficeForApi";

export interface ITaskCreatePayload {
  applicant_id: number;
  assignee_id: number;
  executor_id: number;
  whoose: string;
  type: string;
  intake: string;
  admission_semester: string;
  form_of_education: string;
  selected_course: string;
  category: string;
  medium_of_instruction: string;
  duration: string;
  transfer_fee: string;
  priority: string;
  note: string;
  deadline_date: string;
  attached_file?: string | null;
  tuition_fee?: string;
  tuition_fee_flag?: boolean;
  medical_insurance?: string;
  residence_permit?: string;
  other_expenses?: string;
  acommodation?: string;
  admission_fee?: string;
  complete_one_year?: string;
}

/** PUT /tasks/update/{task_id} — only changed fields are sent. */
export interface ITaskUpdatePayload {
  applicant_id?: number;
  assignee_id?: number;
  executor_id?: number;
  type?: string;
  priority?: string;
  note?: string;
  deadline_date?: string;
  status?: string;
  attached_file?: string | null;
  whoose?: string;
  form_of_education?: string;
  duration?: string;
  medium_of_instruction?: string;
  admission_semester?: string;
  intake?: string;
  category?: string;
  tuition_fee?: string;
  tuition_fee_flag?: boolean;
  medical_insurance?: string;
  residence_permit?: string;
  other_expenses?: string;
  acommodation?: string;
  admission_fee?: string;
  complete_one_year?: string;
  selected_course?: string;
  transfer_fee?: string;
}

export type TaskOffice = "MALTA" | "UKRAINE" | "POLAND" | "ALL";

/** GET /tasks/list — row in `data` */
export interface ITaskListItem {
  id: string;
  applicant_id: string;
  assignee_id: string;
  executor_id: string;
  type: string;
  priority: string;
  note: string;
  deadline_date: string;
  status: string;
  attached_file: string | null;
  whoose: string;
  form_of_education: string;
  duration: string;
  medium_of_instruction: string;
  admission_semester: string;
  intake: string;
  category: string;
  tuition_fee: string;
  tuition_fee_flag?: boolean;
  medical_insurance: string;
  residence_permit: string;
  other_expenses: string;
  acommodation: string;
  admission_fee: string;
  complete_one_year: string;
  selected_course: string;
  transfer_fee: string;
  created_at: string;
  updated_at: string;
  applicant_info: unknown;
  assignee: unknown;
  executor: unknown;
}

/** GET /tasks/list response */
export interface ITasksListResponse {
  data: ITaskListItem[];
  count: number;
}

const normalizeTaskUpdatePayload = (data: ITaskUpdatePayload): ITaskUpdatePayload => {
  if (data.status == null) return data;

  const mappedStatus = mapTaskUiStatusToApi(data.status);
  if (!mappedStatus) return data;

  return { ...data, status: mappedStatus };
};

export const tasksPageRequests = {
  getList: async (limit: string, offset: number, officeUrl?: string) => {
    try {
      return await apiType.main.get<ITasksListResponse>(`/tasks/list`, {
        limit,
        offset,
        ...withOfficeUrlQuery(officeUrl),
      });
    } catch (error) {
      console.log("Tasks getList error:", error);
      return null;
    }
  },
  /** GET /tasks/{task_id} */
  fetchById: async (taskId: string): Promise<ITaskListItem | null> => {
    const normalizedId = taskId.trim();
    if (!normalizedId) return null;

    try {
      const response = await apiType.main.get<ITaskListItem | { data: ITaskListItem }>(`/tasks/${normalizedId}`);
      if (response && typeof response === "object" && "data" in response && response.data) {
        return response.data;
      }
      return response as ITaskListItem;
    } catch (error) {
      console.log("Tasks fetchById error:", error);
      return null;
    }
  },
  getById: async (taskId: string): Promise<ITaskListItem | null> => {
    const fromDirect = await tasksPageRequests.fetchById(taskId);
    if (fromDirect) return fromDirect;

    const normalizedId = taskId.trim();
    if (!normalizedId) return null;

    const pageSize = 100;
    let offset = 0;

    while (offset <= 1000) {
      const list = await tasksPageRequests.getList(String(pageSize), offset);
      if (!list) return null;

      const rows = Array.isArray(list) ? list : Array.isArray(list.data) ? list.data : [];
      const match = findTaskInList(rows, normalizedId);
      if (match) return match;

      if (rows.length < pageSize) break;
      offset += pageSize;
    }

    return null;
  },
  applyFilters: async (limit: string, offset: number, office: string, data: object = {}, officeUrl?: string) => {
    try {
      return await apiType.main.post<ITasksListResponse>(`/tasks/filters/apply`, data, {
        params: {
          limit,
          offset,
          ...withOfficeQuery(office),
          ...withOfficeUrlQuery(officeUrl),
        },
      });
    } catch (error) {
      console.log("Tasks applyFilters error:", error);
      return null;
    }
  },
  update: async (taskId: string, data: ITaskUpdatePayload) => {
    const body = normalizeTaskUpdatePayload(data);
    console.groupCollapsed(`[PUT /tasks/update/${taskId}] Request payload`);
    console.log("endpoint:", `/tasks/update/${taskId}`);
    console.log("body:", body);
    console.groupEnd();
    try {
      return await apiType.main.put(`/tasks/update/${taskId}`, body);
    } catch (error) {
      const axiosError = error as { response?: { data?: unknown } };
      if (axiosError.response?.data != null) {
        console.error(`[PUT /tasks/update/${taskId}] Validation error:`, axiosError.response.data);
      }
      throw error;
    }
  },
  delete: (taskId: string) => apiType.main.delete(`/tasks/delete/${taskId}`),
  create: async (data: ITaskCreatePayload) => {
    return apiType.main.post("/tasks/create", data);
  },
};
